import React, {useState} from 'react';
import {ScrollView, TextInput, TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import Card from '@/Atoms/Card';
import CCText from '@/Atoms/CCText';
import Spacer from '@/Atoms/Spacer';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {STARTING_FEN} from '@/Utils/CommonUtils';
import ChapterSettingsPanel from '../ChapterSettingsPanel';
import {styles} from '../../styles';
import {useStudySettings} from '../../hooks/useStudySettings';

type SettingsActionRowProps = {
  icon: string;
  title: string;
  subtitle?: string;
  actionLabel: string;
  actionIcon?: string;
  actionColor?: string;
  actionTextColor?: string;
  onPress: () => void;
  disabled?: boolean;
  isLast?: boolean;
};

function SettingsActionRow({
  icon,
  title,
  subtitle,
  actionLabel,
  actionIcon,
  actionColor = CCColors.Primary.Brown,
  actionTextColor = CCColors.White,
  onPress,
  disabled,
  isLast,
}: SettingsActionRowProps) {
  return (
    <React.Fragment>
      <Box style={styles.settingsRow}>
        <Box style={styles.settingsRowIcon}>
          <Ionicons
            name={icon as any}
            size={15}
            color={CCColors.Primary.Brown}
          />
        </Box>
        <Box style={{flex: 1}}>
          <CCText
            fontSize={13}
            fontWeight="semibold"
            color={CCColors.TextColor.Primary}>
            {title}
          </CCText>
          {!!subtitle && (
            <CCText
              fontSize={11.5}
              color={CCColors.Workbook.Brown}
              numberOfLines={1}>
              {subtitle}
            </CCText>
          )}
        </Box>
        <TouchableOpacity
          onPress={onPress}
          disabled={disabled}
          style={{
            backgroundColor: actionColor,
            paddingVertical: 7,
            paddingHorizontal: 13,
            borderRadius: 8,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 5,
            opacity: disabled ? 0.5 : 1,
          }}>
          {!!actionIcon && (
            <Ionicons
              name={actionIcon as any}
              size={12}
              color={actionTextColor}
            />
          )}
          <CCText fontSize={11.5} color={actionTextColor} fontWeight="semibold">
            {actionLabel}
          </CCText>
        </TouchableOpacity>
      </Box>
      {!isLast && <Box style={styles.settingsRowDivider} />}
    </React.Fragment>
  );
}

type ShareExportSectionProps = {
  settings: ReturnType<typeof useStudySettings>;
  visibility: string | undefined;
  currentFen: string;
};

// The Settings tab's "Share & export" sub-tab - grouped into labeled
// cards (Workbook details / Visibility / Share / Export) like a native
// settings screen, instead of six separately-bordered boxes each
// floating with their own gap.
function ShareExportSection({
  settings,
  visibility,
  currentFen,
}: ShareExportSectionProps) {
  return (
    <Box style={{gap: 18}}>
      <Box>
        <CCText style={styles.settingsGroupLabel}>Workbook details</CCText>
        <Card style={{overflow: 'hidden'}} padding={12}>
          <CCText
            fontSize={12}
            fontWeight="semibold"
            color={CCColors.TextColor.Primary}>
            Name
          </CCText>
          <Spacer spacing={6} />
          <TextInput
            value={settings.editStudyName}
            onChangeText={settings.setEditStudyName}
            placeholder="Workbook name"
            placeholderTextColor={CCColors.TextColor.Grey}
            style={
              {
                height: 38,
                fontSize: 13,
                color: CCColors.TextColor.Primary,
                backgroundColor: CCColors.Primary.Light,
                borderRadius: 8,
                paddingHorizontal: 10,
                outlineStyle: 'none',
                borderWidth: 1,
                borderColor: updateColorWithOpacity(
                  CCColors.TextColor.Primary,
                  0.15,
                ),
              } as any
            }
          />
          <Spacer spacing={12} />
          <CCText
            fontSize={12}
            fontWeight="semibold"
            color={CCColors.TextColor.Primary}>
            Description
          </CCText>
          <Spacer spacing={6} />
          <TextInput
            value={settings.editStudyDescription}
            onChangeText={settings.setEditStudyDescription}
            placeholder="Shown to viewers"
            placeholderTextColor={CCColors.TextColor.Grey}
            multiline
            numberOfLines={2}
            style={
              {
                height: 56,
                fontSize: 13,
                color: CCColors.TextColor.Primary,
                backgroundColor: CCColors.Primary.Light,
                borderRadius: 8,
                paddingHorizontal: 10,
                paddingVertical: 8,
                outlineStyle: 'none',
                borderWidth: 1,
                borderColor: updateColorWithOpacity(
                  CCColors.TextColor.Primary,
                  0.15,
                ),
              } as any
            }
          />
          <Spacer spacing={12} />
          <TouchableOpacity
            onPress={settings.handleSaveStudySettings}
            disabled={settings.isSavingStudySettings}
            style={{
              backgroundColor: CCColors.Primary.Default,
              paddingVertical: 10,
              borderRadius: 8,
              alignItems: 'center',
              opacity: settings.isSavingStudySettings ? 0.5 : 1,
            }}>
            <CCText
              fontSize={12}
              color={CCColors.TextColor.Primary}
              fontWeight="semibold">
              Save
            </CCText>
          </TouchableOpacity>
        </Card>
      </Box>

      <Box>
        <CCText style={styles.settingsGroupLabel}>Visibility</CCText>
        <Card style={{overflow: 'hidden'}}>
          <SettingsActionRow
            icon={
              visibility === 'private' ? 'lock-closed-outline' : 'earth-outline'
            }
            title={visibility === 'private' ? 'Private' : 'Public'}
            subtitle={
              visibility === 'private'
                ? 'Only you and participants can view this workbook.'
                : 'Any signed-in user can view this workbook.'
            }
            actionLabel={
              visibility === 'private' ? 'Make public' : 'Make private'
            }
            onPress={settings.handleToggleVisibility}
            disabled={settings.isUpdatingVisibility}
            isLast
          />
        </Card>
      </Box>

      <Box>
        <CCText style={styles.settingsGroupLabel}>Share</CCText>
        <Card style={{overflow: 'hidden'}}>
          <SettingsActionRow
            icon="link-outline"
            title="Share link"
            subtitle={window.location.href}
            actionLabel="Copy"
            onPress={settings.handleCopyShareLink}
          />
          <SettingsActionRow
            icon="grid-outline"
            title="Current position"
            subtitle={currentFen}
            actionLabel="Copy"
            onPress={settings.handleCopyFen}
            isLast
          />
        </Card>
      </Box>

      <Box>
        <CCText style={styles.settingsGroupLabel}>Export</CCText>
        <Card style={{overflow: 'hidden'}}>
          <SettingsActionRow
            icon="download-outline"
            title="This chapter"
            subtitle="Download this chapter's moves as PGN."
            actionLabel="PGN"
            actionIcon="download-outline"
            actionColor={CCColors.Primary.Default}
            actionTextColor={CCColors.TextColor.Primary}
            onPress={settings.handleExportPgn}
          />
          <SettingsActionRow
            icon="layers-outline"
            title="Full workbook"
            subtitle="Every chapter as one multi-game PGN file."
            actionLabel="PGN"
            actionIcon="download-outline"
            actionColor={CCColors.Primary.Default}
            actionTextColor={CCColors.TextColor.Primary}
            onPress={settings.handleExportStudyPgn}
            isLast
          />
        </Card>
      </Box>
    </Box>
  );
}

type WorkbookSettingsTabProps = {
  settings: ReturnType<typeof useStudySettings>;
  studyDetails: any;
  selectedChapter: any;
  moveSelected: any;
  gameId: any;
  setLoading: (loading: boolean) => void;
  refetchStudy: () => void;
  setSelectedChapter: (chapter: any) => void;
};

// The Workbook panel's Settings tab - a pill switcher between Chapter
// config and workbook-level Share & Export, each its own scroll, instead
// of both sections stacked in one long page.
function WorkbookSettingsTab({
  settings,
  studyDetails,
  selectedChapter,
  moveSelected,
  gameId,
  setLoading,
  refetchStudy,
  setSelectedChapter,
}: WorkbookSettingsTabProps) {
  const [settingsSubTab, setSettingsSubTab] = useState<'chapter' | 'share'>(
    'chapter',
  );

  return (
    <Box style={{flex: 1}}>
      <Box style={{padding: 12, paddingBottom: 8}}>
        <Box style={styles.settingsSubTabPill}>
          <TouchableOpacity
            onPress={() => setSettingsSubTab('chapter')}
            style={[
              styles.settingsSubTabBtn,
              settingsSubTab === 'chapter' && styles.settingsSubTabBtnActive,
            ]}>
            <MaterialCommunityIcons
              name="chess-rook"
              size={13}
              color={
                settingsSubTab === 'chapter'
                  ? CCColors.TextColor.Primary
                  : CCColors.Workbook.Brown
              }
            />
            <CCText
              style={[
                styles.settingsSubTabLabel,
                settingsSubTab === 'chapter' &&
                  styles.settingsSubTabLabelActive,
              ]}>
              Chapter
            </CCText>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setSettingsSubTab('share')}
            style={[
              styles.settingsSubTabBtn,
              settingsSubTab === 'share' && styles.settingsSubTabBtnActive,
            ]}>
            <MaterialCommunityIcons
              name="share-variant"
              size={13}
              color={
                settingsSubTab === 'share'
                  ? CCColors.TextColor.Primary
                  : CCColors.Workbook.Brown
              }
            />
            <CCText
              style={[
                styles.settingsSubTabLabel,
                settingsSubTab === 'share' && styles.settingsSubTabLabelActive,
              ]}>
              Share & export
            </CCText>
          </TouchableOpacity>
        </Box>
      </Box>

      {settingsSubTab === 'chapter' ? (
        <ScrollView style={{flex: 1}} showsVerticalScrollIndicator={false}>
          <ChapterSettingsPanel
            chapter={selectedChapter}
            gameId={gameId}
            setLoading={setLoading}
            refetchStudy={refetchStudy}
            setSelectedChapter={setSelectedChapter}
            boardFen={moveSelected?.fen || selectedChapter?.fen || STARTING_FEN}
            boardPositionLabel={
              !moveSelected?.id || moveSelected?.id === 'start'
                ? 'The chapter start.'
                : `After ${
                    moveSelected?.san || moveSelected?.move || 'this move'
                  }.`
            }
            chapterStartFen={selectedChapter?.fen || STARTING_FEN}
          />
        </ScrollView>
      ) : (
        <ScrollView style={{flex: 1}} showsVerticalScrollIndicator={false}>
          <Box style={{padding: 12, paddingTop: 4}}>
            <ShareExportSection
              settings={settings}
              visibility={studyDetails?.data?.visibility}
              currentFen={
                moveSelected?.fen || selectedChapter?.fen || STARTING_FEN
              }
            />
          </Box>
        </ScrollView>
      )}
    </Box>
  );
}

export default WorkbookSettingsTab;
